import React, { useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { Add, Star, CheckCircle, Error, Check, Close } from "@mui/icons-material";

/**
 * Renders the grid of supported accounting platforms and their active connections.
 * 
 * @param {Object} props
 * @param {Array} props.platforms - Available accounting platforms
 * @param {Array} props.connections - Active user connections
 * @param {Object} props.selectedConnection - Currently focused connection
 * @param {boolean} props.writePermission - Controls edit capabilities
 * @param {boolean} props.canSyncContacts - Controls sync button state
 * @param {Function} props.selectConnection - Callback to select a connection
 * @param {Function} props.handleConnect - Callback to initiate connection flow
 * @param {Function} props.handleSetDefault - Callback to set connection as default
 * @param {Function} props.handleToggleActive - Callback to toggle connection status
 * @param {Function} props.handleSyncConn - Callback to force sync
 * @param {Function} props.handleDisconnect - Callback to disconnect platform
 */

const PlatformCardHeader = ({ platform, connectionCount }) => (
    <div className="d-flex align-items-center">
        <div
            className="platform-logo-circle d-flex align-items-center justify-content-center text-white font-weight-bold"
            style={{ width: 50, height: 50, borderRadius: '50%', backgroundColor: platform.logoColor, fontSize: '1.2rem', marginRight: 15 }}
        >
            {platform.logoText}
        </div>
        <div>
            <h5 className="mb-1 font-weight-bold">{platform.name}</h5>
            <p className="mb-0 small">
                {connectionCount} connected organisation{connectionCount === 1 ? '' : 's'}
            </p>
        </div>
    </div>
);

export default function ConnectionPlatforms({
    platforms,
    connections,
    selectedConnection,
    writePermission = true,
    canSyncContacts = true,
    selectConnection,
    handleConnect,
    handleSetDefault,
    handleToggleActive,
    handleSyncConn,
    handleDisconnect
}) {
    const [confirmConnId, setConfirmConnId] = useState(null);
    return (
        <Row>
            {platforms.map((platform) => {
                const platformConnections = (connections || []).filter((conn) => conn.platform === platform.key);

                if (!platform.isSupported) {
                    return (
                        <Col xl={4} md={6} xs={12} className="mb-3" key={platform.key}>
                            <Card className="shadow-sm border-0 mb-3 h-100 opacity-75 integration-platform-card disabled-platform">
                                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <PlatformCardHeader platform={platform} connectionCount={platformConnections.length} />
                                        <div>
                                            <Button variant="outline-secondary" size="sm" disabled data-cy={`connect-disabled-${platform.key}-btn`}>
                                                Coming Soon
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="connections-list">
                                        <div className="text-center py-5 small border border-dashed rounded-3 text-muted">
                                            Integration coming soon
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                }

                return (
                    <Col xl={4} md={6} xs={12} className="mb-3" key={platform.key}>
                        <Card className="shadow-sm border-0 mb-3 h-100 integration-platform-card">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <PlatformCardHeader platform={platform} connectionCount={platformConnections.length} />
                                    <div>
                                        {platformConnections.length > 0 ? (
                                            <Button variant="outline-primary" size="sm" onClick={() => handleConnect(platform.key, false)} disabled={!writePermission} className="d-flex align-items-center gap-1" data-cy={`connect-another-${platform.key}-btn`}>
                                                <Add style={{ fontSize: '0.875rem' }} /> Connect Another
                                            </Button>
                                        ) : (
                                            <Button variant="outline-primary" size="sm" onClick={() => handleConnect(platform.key, true)} disabled={!writePermission} className="d-flex align-items-center gap-1" data-cy={`connect-${platform.key}-btn`}>
                                                <Add style={{ fontSize: '0.875rem' }} /> Connect
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                <div className="connections-list">
                                    {platformConnections.map((conn) => {
                                        const isSelected = selectedConnection && selectedConnection.id === conn.id;
                                        return (
                                            <div
                                                key={conn.id}
                                                className={`p-3 rounded mb-2 border connection-org-row ${isSelected ? 'border-primary bg-light shadow-sm selected-org-row' : 'border-secondary-subtle'}`}
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease-in-out',
                                                    borderLeftWidth: isSelected ? '4px' : '1px',
                                                    transform: isSelected ? 'translateY(-1px)' : 'none'
                                                }}
                                                onClick={() => {
                                                    if (selectedConnection?.id !== conn.id) {
                                                        selectConnection(conn);
                                                    }
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.borderColor = '#0d6efd';
                                                        e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.borderColor = '#dee2e6';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                                data-cy={`connection-row-${conn.id}`}
                                            >
                                                {/* Row 1: Company details and Active toggle switch */}
                                                <div className="d-flex align-items-start justify-content-between gap-3">
                                                    <div className="min-w-0 flex-grow-1">
                                                        <div
                                                            className={`font-weight-bold text-truncate org-display-name ${isSelected ? 'text-primary' : 'text-dark'}`}
                                                            style={{ fontSize: '0.95rem' }}
                                                            title={conn.display_name}
                                                        >
                                                            {conn.display_name}
                                                        </div>
                                                        <div className="mt-1 d-flex flex-wrap gap-1 badge-list">
                                                            {conn.is_syncing && <span className="badge bg-warning text-dark small badge-syncing" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>Syncing...</span>}
                                                            {isSelected && <span className="badge bg-warning text-dark small badge-configuring" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>Configuring</span>}
                                                            {conn.is_default ? (
                                                                <span className="badge bg-success small badge-default" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                                                                    <Star style={{ fontSize: '0.75rem', marginRight: '2px' }} /> Default
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="badge-set-default-btn"
                                                                    data-cy={`set-default-btn-${conn.id}`}
                                                                    disabled={!writePermission || conn.is_syncing === true}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!writePermission || conn.is_syncing) return;
                                                                        handleSetDefault(conn);
                                                                    }}
                                                                >
                                                                    Set Default
                                                                </button>
                                                            )}
                                                            {conn.is_expired ? (
                                                                <span className="badge bg-danger small badge-expired" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                                                                    <Error style={{ fontSize: '0.75rem', marginRight: '2px' }} /> Session Expired
                                                                </span>
                                                            ) : (
                                                                !isSelected && <span className="badge bg-success small badge-connected" style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}><CheckCircle style={{ fontSize: '0.75rem', marginRight: '2px' }} /> Connected</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0 pt-1">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`${platform.key}-active-${conn.id}`}
                                                            checked={conn.is_active}
                                                            onChange={(e) => handleToggleActive(conn, e)}
                                                            disabled={!writePermission || conn.is_syncing === true}
                                                            className="custom-switch-lg mb-0"
                                                            data-cy={`active-switch-${conn.id}`}
                                                            title={conn.is_active ? "Enabled" : "Disabled"}
                                                        />
                                                    </div>
                                                </div>
 
                                                {/* Row 2: Action buttons and sync status footer */}
                                                <div className="d-flex justify-content-between align-items-center gap-2 mt-3 pt-2 border-top border-light org-row-footer" onClick={(e) => e.stopPropagation()}>
                                                    <span className="small last-sync-text">
                                                        Last sync {conn.updatedAt ? new Date(conn.updatedAt).toLocaleDateString() : 'Never'}
                                                    </span>
                                                    <div className="d-flex gap-2">
                                                        {conn.is_active && !conn.is_expired && (
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleSyncConn(conn)}
                                                                disabled={!canSyncContacts || conn.is_syncing === true}
                                                                data-cy={`sync-btn-${conn.id}`}
                                                            >
                                                                {conn.is_syncing ? 'Syncing...' : 'Sync'}
                                                            </Button>
                                                        )}
                                                        {conn.is_expired ? (
                                                            <Button
                                                                variant="outline-warning"
                                                                size="sm"
                                                                onClick={() => handleConnect(platform.key, true)}
                                                                disabled={!writePermission}
                                                                data-cy={`reconnect-btn-${conn.id}`}
                                                            >
                                                                Reconnect
                                                            </Button>
                                                        ) : (
                                                            <OverlayTrigger
                                                                trigger="click"
                                                                placement="top"
                                                                rootClose
                                                                show={confirmConnId === conn.id}
                                                                onToggle={(nextShow) => setConfirmConnId(nextShow ? conn.id : null)}
                                                                overlay={
                                                                    <Popover id={`popover-disconnect-${conn.id}`} className="shadow border p-1">
                                                                        <Popover.Body className="p-2 text-center">
                                                                            <div className="fw-semibold mb-2" style={{ fontSize: '0.9rem' }}>Are you sure?</div>
                                                                            <div className="d-flex justify-content-center gap-2">
                                                                                <Button
                                                                                    variant="primary"
                                                                                    size="sm"
                                                                                    className="d-inline-flex align-items-center px-2 py-1"
                                                                                    onClick={() => {
                                                                                        setConfirmConnId(null);
                                                                                        handleDisconnect(conn);
                                                                                    }}
                                                                                >
                                                                                    <Check style={{ fontSize: '0.95rem', marginRight: '3px' }} /> Yes
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="d-inline-flex align-items-center px-2 py-1"
                                                                                    onClick={() => setConfirmConnId(null)}
                                                                                >
                                                                                    <Close style={{ fontSize: '0.95rem', marginRight: '3px' }} /> No
                                                                                </Button>
                                                                            </div>
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    disabled={!writePermission || conn.is_syncing === true}
                                                                    data-cy={`disconnect-btn-${conn.id}`}
                                                                >
                                                                    Disconnect
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {platformConnections.length === 0 && (
                                        <div className="small text-center py-4 border border-dashed rounded-3 no-orgs-connected">
                                            No organisations connected yet.
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
}
